import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import FiltroBar from "../../components/FiltroBar/FiltroBar";
import { db } from "../../config/firebase";
import { collection } from "firebase/firestore";

const COLLECTION_CARTOES = "financeiro_cartoes_credito";

const STATUS_OPTIONS = ["ATIVO", "INATIVO"];

function toNumber(value) {
  const n = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(value) {
  const n = Number(value || 0);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function FinanceiroCartoesCreditoPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const { empresaAtual, modoGlobal } = useEmpresa();
  const navigate = useNavigate();

  const [clientes, setClientes] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [campoFiltro, setCampoFiltro] = useState("nomeCartao");
  const [textoFiltro, setTextoFiltro] = useState("");

  const restringirEmpresa = useMemo(() => {
    const nivel = nivelUsuario(usuario);
    return !modoGlobal || nivel < NIVEIS.TECNICO_SENIOR;
  }, [modoGlobal, usuario]);

  const empresaEscopoId = empresaAtual?.id || usuario?.empresaId || "";

  useEffect(() => {
    const carregarClientes = async () => {
      if (!usuario?.grupoId) return;
      try {
        const snap = await getDocs(
          query(collection(db, "empresas"), where("grupoId", "==", usuario.grupoId))
        );
        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
        setClientes(lista);
      } catch (e) {
        console.error("Erro ao carregar empresas:", e);
      }
    };

    carregarClientes();
  }, [usuario?.grupoId]);

  useEffect(() => {
    if (restringirEmpresa) {
      // no-op: empresaId não é mais utilizado
    }
  }, [restringirEmpresa, empresaEscopoId]);

  useEffect(() => {
    const carregar = async () => {
      if (!usuario?.grupoId) return;
      setCarregando(true);
      setErro("");
      try {
        const snap = await getDocs(
          query(collection(db, COLLECTION_CARTOES), where("grupoId", "==", usuario.grupoId))
        );

        let lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        if (restringirEmpresa && empresaEscopoId) {
          const ref = String(empresaEscopoId);
          lista = lista.filter((item) => String(item?.empresaId || "") === ref);
        }

        lista.sort((a, b) => String(a.nomeCartao || "").localeCompare(String(b.nomeCartao || ""), "pt-BR"));
        setRegistros(lista);
      } catch (e) {
        console.error("Erro ao carregar cartões:", e);
        setErro("Erro ao carregar cartões.");
      } finally {
        setCarregando(false);
      }
    };

    carregar();
  }, [usuario?.grupoId, restringirEmpresa, empresaEscopoId]);

  const camposFiltro = [
    { value: "nomeCartao", label: "Cartão" },
    { value: "bandeira", label: "Bandeira" },
    { value: "bancoEmissor", label: "Banco" },
    { value: "status", label: "Status" },
  ];

  const filtrados = useMemo(() => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return registros;
    return registros.filter((r) => String(r?.[campoFiltro] ?? "").toLowerCase().includes(texto));
  }, [registros, campoFiltro, textoFiltro]);

  const handleExcluir = async (registro) => {
    const confirmar = window.confirm("Deseja excluir este cartão?");
    if (!confirmar) return;
    setErro("");
    setSucesso("");

    try {
      await deleteDoc(doc(db, COLLECTION_CARTOES, registro.id));
      setRegistros((prev) => prev.filter((item) => item.id !== registro.id));
      setSucesso("Cartão excluído com sucesso.");
    } catch (e) {
      console.error("Erro ao excluir cartão:", e);
      setErro("Erro ao excluir cartão.");
    }
  };

  return (
    <LayoutDesktop tema={tema} titulo="Cartões" subtitulo="Financeiro / Cadastros / Cartões de Crédito">
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Cartões de crédito</h3>
            <p>Cadastro e gestão dos cartões por empresa, com limites e ciclo de fatura.</p>
          </div>
          <div className="pc-table-header-actions">
            <button type="button" className="pc-btn-primario" onClick={() => navigate("/financeiro/cartoes-credito/novo")}>
              + Adicionar cartão
            </button>
          </div>
        </header>

        {erro && <div className="pc-alert pc-alert-erro">{erro}</div>}
        {sucesso && <div className="pc-alert pc-alert-sucesso">{sucesso}</div>}

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          placeholder="Filtrar cartões..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          <table className="pc-table pc-table-expandable">
            <thead>
              <tr>
                <th>Cartão</th>
                <th>Bandeira</th>
                <th>Banco</th>
                <th>Limite total</th>
                <th>Limite disponível</th>
                <th>Fechamento/Venc.</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr>
                  <td colSpan={8} style={{ padding: 16 }}>Carregando cartões...</td>
                </tr>
              ) : filtrados.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 16 }}>Nenhum cartão encontrado.</td>
                </tr>
              ) : (
                filtrados.map((item) => {
                  const aberto = expandedId === item.id;
                  return (
                    <React.Fragment key={item.id}>
                      <tr className={aberto ? "pc-row-open" : undefined}>
                        <td>{item.nomeCartao || "-"}</td>
                        <td>{item.bandeira || "-"}</td>
                        <td>{item.bancoEmissor || "-"}</td>
                        <td>R$ {formatMoney(item.limiteTotal)}</td>
                        <td>R$ {formatMoney(item.limiteDisponivel)}</td>
                        <td>{item.diaFechamento || "-"} / {item.diaVencimento || "-"}</td>
                        <td>{item.status || "-"}</td>
                        <td>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              type="button"
                              className="pc-btn-secundario"
                              onClick={() => setExpandedId((prev) => (prev === item.id ? null : item.id))}
                            >
                              {aberto ? "Ocultar" : "Detalhes"}
                            </button>
                            <button type="button" className="pc-btn-secundario" onClick={() => navigate(`/financeiro/cartoes-credito/editar/${item.id}`)}>
                              Editar
                            </button>
                            <button type="button" className="pc-btn-perigo" onClick={() => handleExcluir(item)}>
                              Excluir
                            </button>
                          </div>
                        </td>
                      </tr>
                      {aberto && (
                        <tr className="pc-expanded-row">
                          <td colSpan={8}>
                            <div className="pc-expand-panel">
                              <div className="pc-expand-grid">
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Empresa</div>
                                  <div className="pc-expand-value">{item.clienteNome || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Conta de pagamento</div>
                                  <div className="pc-expand-value">{item.contaPagamento || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Final do cartão</div>
                                  <div className="pc-expand-value">{item.finalCartao || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Limite utilizado</div>
                                  <div className="pc-expand-value">
                                    R$ {formatMoney((Number(item.limiteTotal || 0) - Number(item.limiteDisponivel || 0)))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </LayoutDesktop>
  );
}
